import React, { useState } from "react";
import { Box, Typography, Button, TextField, Modal } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import { fetchCreateOptionValue } from "../../store/reducers/createProfile/apithunk";
import { ToastNotifyError, ToastNotifySuccess } from "../../components/CommonComponents/Toast/ToastNotify";
import rollbar from "../../rollbarConfig";

const AddNewVariantValueModal = ({ open, onClose, onSuccess, optionTypeId, isColorVariant = false, currentOptionCount = 0 }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  // Start with one option field (current + 1)
  const [variantOptions, setVariantOptions] = useState([{ value: "", color: "" }]);
  const [isLoading, setIsLoading] = useState(false);

  const handleAddMoreOption = () => {
    setVariantOptions([...variantOptions, { value: "", color: "" }]);
  };

  const handleOptionChange = (index, field, newValue) => {
    const updated = [...variantOptions];
    updated[index] = { ...updated[index], [field]: newValue };
    setVariantOptions(updated);
  };

  const handleRemoveOption = (index) => {
    if (variantOptions?.length > 1) {
      setVariantOptions(variantOptions?.filter((_, i) => i !== index));
    }
  };

  const handleAdd = async () => {
    if (!optionTypeId) {
      ToastNotifyError("Option type ID is required");
      return;
    }

    const validOptions = variantOptions?.filter(opt => opt?.value?.trim());
    if (validOptions?.length === 0) {
      ToastNotifyError("At least one variant option is required");
      return;
    }

    setIsLoading(true);
    try {
      // Create option values
      const valuePromises = validOptions?.map((option, index) => {
        const payload = {
          option_type: optionTypeId,
          value: option?.value?.trim(),
          position: index + 1,
          is_active: true,
          hex_code: isColorVariant && option?.color ? option?.color : null,
          metadata: {}
        };
        return dispatch(fetchCreateOptionValue(payload));
      });

      const valueResponses = await Promise.all(valuePromises);
      const allSuccess = valueResponses?.every(res => {
        const { status } = res?.payload || {};
        return status;
      });

      if (!allSuccess) {
        ToastNotifyError("Some option values failed to create");
        setIsLoading(false);
        return;
      }

      ToastNotifySuccess("Variant values added successfully!");
      
      // Reset form
      setVariantOptions([{ value: "", color: "" }]);
      
      // Notify parent to refresh options
      if (onSuccess) {
        onSuccess();
      }
      
      // Close modal after successful addition
      onClose();
    } catch (error) {
      rollbar.error("Error creating variant values", error);
      ToastNotifyError("Failed to create variant values");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setVariantOptions([{ value: "", color: "" }]);
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        // Only close on Cancel button click, not on backdrop click
        if (reason === 'backdropClick') {
          return;
        }
        handleClose();
      }}
      closeAfterTransition
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(3px)",
      }}
    >
      <Box
        sx={{
          position: 'relative',
          backgroundColor: 'white',
          borderRadius: '8px',
          p: 3,
          maxWidth: '500px',
          width: '90vw',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <Typography sx={{
          fontFamily: 'Lexend',
          fontSize: '20px',
          fontWeight: 500,
          mb: 3,
          color: theme.palette?.primary?.main || '#0F0F0F',
        }}>
          Add New Variant Value
        </Typography>

        {/* Variant Options */}
        {variantOptions?.map((option, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            <Typography sx={{
              fontFamily: 'Lexend',
              fontSize: '14px',
              fontWeight: 400,
              mb: 1,
              color: theme.palette?.primary?.main || '#0F0F0F',
            }}>
              Variant Option {currentOptionCount + index + 1}<span style={{ color: '#D32F2F' }}>*</span>
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              {isColorVariant && (
                <Box
                  component="input"
                  type="color"
                  value={option?.color || '#000000'}
                  onChange={(e) => handleOptionChange(index, 'color', e.target.value)}
                  disabled={isLoading}
                  sx={{
                    width: '50px',
                    height: '40px',
                    border: `1px solid ${theme.palette?.customColor?.secondaryThird || "#E0E0E0"}`,
                    borderRadius: '4px',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                />
              )}
              <TextField
                fullWidth
                placeholder="Add Variant Option Name"
                value={option?.value}
                onChange={(e) => handleOptionChange(index, 'value', e.target.value)}
                disabled={isLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'Lexend',
                    fontSize: '14px',
                  }
                }}
              />
              {variantOptions?.length > 1 && (
                <Button
                  onClick={() => handleRemoveOption(index)}
                  disabled={isLoading}
                  sx={{
                    minWidth: 'auto',
                    color: theme.palette?.customColor?.red || '#D32F2F',
                    fontFamily: 'Lexend',
                    fontSize: '14px',
                    textTransform: 'none',
                  }}
                >
                  Remove
                </Button>
              )}
            </Box>
          </Box>
        ))}

        {/* Add More Option Button */}
        <Button
          onClick={handleAddMoreOption}
          disabled={isLoading}
          sx={{
            fontFamily: 'Lexend',
            fontSize: '14px',
            fontWeight: 400,
            color: theme.palette?.primary?.main || '#0F0F0F',
            textTransform: 'none',
            padding: 0,
            minWidth: 'auto',
            mb: 3,
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline',
            }
          }}
        >
          +Add More Option
        </Button>

        {/* Action Buttons */}
        <Box sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end',
        }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={isLoading}
            sx={{
              borderColor: theme.palette?.customColor?.secondaryThird || "#E0E0E0",
              color: theme.palette?.primary?.main || '#0F0F0F',
              fontFamily: 'Lexend',
              fontSize: '14px',
              fontWeight: 400,
              textTransform: 'none',
              borderRadius: '4px',
              px: 3,
              py: 1,
              '&:hover': {
                borderColor: theme.palette?.primary?.main || '#0F0F0F',
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={isLoading}
            sx={{
              backgroundColor: theme.palette?.primary?.main || '#0F0F0F',
              color: '#FFFFFF',
              fontFamily: 'Lexend',
              fontSize: '14px',
              fontWeight: 400,
              textTransform: 'none',
              borderRadius: '4px',
              px: 3,
              py: 1,
              '&:hover': {
                backgroundColor: theme.palette?.primary?.dark || '#000000',
              },
              '&:disabled': {
                backgroundColor: theme.palette?.action?.disabled || '#E0E0E0',
              }
            }}
          >
            {isLoading ? "Adding..." : "Add"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddNewVariantValueModal;

